import React, { useEffect, useState, useRef } from "react";
import LoginPage from "./login.page";
import { useRouteMatch, useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  additionalJoin,
  validateInvitationCode,
  setInvitationCode,
  clearAuthError,
  setVerificationEmail,
} from "modules/trackco/redux";
import { selectLoading } from "modules/notification";
import { SIGN_UP_VERIFICATION } from "modules/trackco/config/trackco.route";

function LoginContainer() {
  // Ref to store last attempted email
  const lastAttemptedEmailRef = useRef("");

  const match = useRouteMatch();
  const invitationCode = match.params.invitationCode;
  const history = useHistory();
  const dispatch = useDispatch();

  const distributor = useSelector((state) => state.trackco.inviter.distributor);
  const branch = useSelector((state) => state.trackco.inviter.branch);
  const lang = useSelector((state) => state.constant.languages);
  const isInvitationValid = useSelector(
    (state) => state.trackco.inviter.isInvitationValid
  );
  const isValidateInvitationLoading = useSelector((state) =>
    selectLoading(state, validateInvitationCode.typePrefix)
  );
  const isAuthLoading = useSelector((state) =>
    selectLoading(state, additionalJoin.typePrefix)
  );
  const authError = useSelector((state) => state.trackco.auth?.error);
  const authErrorType = useSelector((state) => state.trackco.auth?.errorType);
  const isAuthenticated = useSelector(
    (state) => state.trackco.auth?.isAuthenticated
  );

  const [isPasswordShown, setIsPasswordShown] = useState(false);

  useEffect(() => {
    return () => {
      dispatch(clearAuthError());
    };
  }, [dispatch]);

  useEffect(() => {
    if (isAuthenticated) {
      history.replace("/trackco/login/success");
    }
  }, [isAuthenticated, history]);

  useEffect(() => {
    let timer;
    if (authError && authErrorType !== "verify_email") {
      timer = setTimeout(() => {
        dispatch(clearAuthError());
      }, 5000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [authError, authErrorType, dispatch]);

  useEffect(() => {
    let authErrorEmail =
      authError?.email || lastAttemptedEmailRef.current || "";
    if (authErrorType === "verify_email") {
      if (authErrorEmail) {
        sessionStorage.setItem("email", authErrorEmail);
        sessionStorage.setItem("needsVerification", "true");
        dispatch(setVerificationEmail({ email: authErrorEmail }));
      }
      const timer = setTimeout(() => {
        dispatch(clearAuthError()); // Clear error before redirecting
        history.push(
          `${SIGN_UP_VERIFICATION}?email=${encodeURIComponent(authErrorEmail)}`
        );
      }, 6000); // Redirect after 6 seconds
      return () => clearTimeout(timer);
    }
  }, [authErrorType, authError, history, dispatch]);

  useEffect(() => {
    document.title = "Login - Trackco @ Customer Portal";

    if (!isInvitationValid) {
      dispatch(validateInvitationCode(invitationCode)).then((response) => {
        if (!response.payload.data.is_valid) {
          history.push("/404");
        } else {
          dispatch(setInvitationCode({ invitationCode: invitationCode }));
        }
      });
    }
  }, [invitationCode, dispatch, isInvitationValid, history]);

  const handleClickShowPassword = () => {
    setIsPasswordShown((prev) => !prev);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleAdditionalJoin = async (values) => {
    lastAttemptedEmailRef.current = values.email;
    const payload = {
      invitationCode: invitationCode,
      email: values.email,
      password: values.password,
    };

    const result = await dispatch(additionalJoin(payload));

    if (additionalJoin.fulfilled.match(result) && result.payload.success) {
      history.push("/trackco/login/success");
    }
  };

  return (
    <LoginPage
      invitationCode={invitationCode}
      distributorDetail={distributor}
      branchDetail={branch}
      lang={lang}
      isValidateInvitationLoading={isValidateInvitationLoading}
      isInvitationCodeValidated={isInvitationValid}
      handleSubmit={handleAdditionalJoin}
      handleClickShowPassword={handleClickShowPassword}
      handleMouseDownPassword={handleMouseDownPassword}
      isPasswordShown={isPasswordShown}
      authError={authError}
      authErrorType={authErrorType}
      isAuthLoading={isAuthLoading}
    />
  );
}

export default LoginContainer;
